/**
 * Eligible Touch Panel device (Edge Agent) discovery helpers.
 *
 * Existing Signal Bridge inventory:
 * - tenants/{tenantId}/devices → Algo / phone / SMS (not ESP32 panels)
 * - agents/{agentId} → Edge Agents (ESP32 / panel form-factor live here)
 *
 * Phase 4 therefore treats agentId as deviceId and does not create a parallel registry.
 */

import {
  TOUCH_PANEL_CAPABILITY_KEY,
  TOUCH_PANEL_SCHEMA_VERSIONS_KEY,
  TOUCH_PANEL_PAYLOAD_LIMITS,
  resolvePanelDisplayFromModel,
} from './payloadLimits';

const ESP_PLATFORMS = new Set(['esp32', 'esp32p4', 'esp-idf', 'esp32-p4']);
const PANEL_FORM_FACTORS = new Set(['panel', 'appliance', 'touch_panel', 'touchpanel']);

function asList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((x) => String(x).trim()).filter(Boolean);
  if (typeof raw === 'string') return raw.trim() ? [raw.trim()] : [];
  return [];
}

function normalizeCaps(raw) {
  if (!raw) return { list: [], map: {} };
  if (Array.isArray(raw)) {
    return { list: asList(raw), map: {} };
  }
  if (typeof raw === 'object') {
    const list = [];
    Object.entries(raw).forEach(([k, v]) => {
      if (v === true) list.push(k);
    });
    return { list, map: raw };
  }
  return { list: [], map: {} };
}

export function extractDeviceId(agent) {
  return agent?.agentId || agent?.id || agent?.deviceId || null;
}

export function extractDisplay(agent) {
  const caps = agent?.capabilities;
  const fromCaps =
    caps && typeof caps === 'object' && !Array.isArray(caps) ? caps.display : null;
  const display = agent?.display || fromCaps || null;
  const modelHint =
    agent?.model ||
    agent?.deviceInfo?.model ||
    agent?.device_info?.model ||
    (typeof caps === 'object' && !Array.isArray(caps) ? caps.boardProfile : null) ||
    agent?.profile;
  let width = Number(
    display?.width ?? agent?.displayWidth ?? agent?.screenWidth ?? NaN
  );
  let height = Number(
    display?.height ?? agent?.displayHeight ?? agent?.screenHeight ?? NaN
  );

  const inferred = resolvePanelDisplayFromModel(modelHint);
  if (!Number.isFinite(width) || !Number.isFinite(height)) {
    if (inferred) {
      width = inferred.width;
      height = inferred.height;
    }
  } else if (
    inferred &&
    width === 720 &&
    height === 720 &&
    (inferred.width !== 720 || inferred.height !== 720)
  ) {
    // Older firmware always reported 720×720 — prefer model-specific profile.
    width = inferred.width;
    height = inferred.height;
  }

  const orientation =
    display?.orientation ||
    agent?.orientation ||
    (Number.isFinite(width) && Number.isFinite(height) && width > height
      ? 'landscape'
      : Number.isFinite(width) && Number.isFinite(height) && width === height
        ? 'landscape'
        : 'portrait');

  return {
    width: Number.isFinite(width) ? width : null,
    height: Number.isFinite(height) ? height : null,
    orientation: orientation || TOUCH_PANEL_PAYLOAD_LIMITS.DEFAULT_DISPLAY.orientation,
  };
}

export function extractSchemaVersions(agent) {
  const caps = agent?.capabilities;
  if (caps && typeof caps === 'object' && !Array.isArray(caps)) {
    const fromObj =
      caps[TOUCH_PANEL_SCHEMA_VERSIONS_KEY] ||
      caps.touchPanelSchemaVersions ||
      caps.schemaVersions?.touchPanel;
    if (Array.isArray(fromObj)) return fromObj.map(Number).filter((n) => !Number.isNaN(n));
  }
  const details = agent?.capabilityDetails;
  if (details && typeof details === 'object') {
    const fromDetails =
      details[TOUCH_PANEL_SCHEMA_VERSIONS_KEY] ||
      details.touchPanelSchemaVersions ||
      details.schemaVersions?.touchPanel;
    if (Array.isArray(fromDetails)) {
      return fromDetails.map(Number).filter((n) => !Number.isNaN(n));
    }
  }
  const direct =
    agent?.touchPanelSchemaVersions ||
    agent?.rendererSchemaVersions ||
    agent?.supportedSchemaVersions;
  if (Array.isArray(direct)) return direct.map(Number).filter((n) => !Number.isNaN(n));
  return [];
}

/**
 * Is this agent a Touch Panel candidate (not merely by display name)?
 */
export function isTouchPanelCandidate(agent) {
  if (!agent || agent.disabled || agent.revokedAt) return false;

  if (agent.touchPanel === true) return true;
  if (agent.touchPanel === false) return false;

  const { list, map } = normalizeCaps(agent.capabilities);
  if (map[TOUCH_PANEL_CAPABILITY_KEY] === true) return true;
  if (list.includes(TOUCH_PANEL_CAPABILITY_KEY) || list.includes('touch.panel')) return true;

  // transformAgentFromApi may only expose capabilities as `features`
  const features = asList(agent.features);
  if (features.includes(TOUCH_PANEL_CAPABILITY_KEY) || features.includes('touch.panel')) {
    return true;
  }

  const platform = String(agent.platform || agent.platformType || '').toLowerCase();
  const formFactor = String(
    agent.formFactor || agent.form_factor || agent.formfactor || ''
  ).toLowerCase();

  if (ESP_PLATFORMS.has(platform)) return true;
  if (PANEL_FORM_FACTORS.has(formFactor)) return true;

  return false;
}

/**
 * Compatibility for assignment UI.
 * @returns {'compatible'|'incompatible'|'unknown'}
 */
export function getTouchPanelCompatibility(agent, { schemaVersion = 1 } = {}) {
  if (!isTouchPanelCandidate(agent)) {
    return 'incompatible';
  }

  const versions = extractSchemaVersions(agent);
  const display = extractDisplay(agent);
  const limits = TOUCH_PANEL_PAYLOAD_LIMITS;

  let knownIncompatible = false;
  let hasUnknown = false;

  const maxBytes = agent?.maxTouchPanelPayloadBytes;
  if (maxBytes != null && Number.isFinite(Number(maxBytes))) {
    if (Number(maxBytes) < limits.MAX_PAYLOAD_BYTES) knownIncompatible = true;
  }

  if (versions.length > 0) {
    if (!versions.includes(Number(schemaVersion))) knownIncompatible = true;
  } else {
    hasUnknown = true;
  }

  if (display.width != null && display.height != null) {
    if (
      display.width < limits.MIN_DISPLAY_WIDTH ||
      display.width > limits.MAX_DISPLAY_WIDTH ||
      display.height < limits.MIN_DISPLAY_HEIGHT ||
      display.height > limits.MAX_DISPLAY_HEIGHT
    ) {
      knownIncompatible = true;
    }
  } else {
    hasUnknown = true;
  }

  if (knownIncompatible) return 'incompatible';
  if (hasUnknown) return 'unknown';
  return 'compatible';
}

/**
 * Normalize an agent into an eligible-device view model for assignment UI.
 */
export function toEligibleDeviceView(agent, { assignment = null, panelNameById = {} } = {}) {
  const deviceId = extractDeviceId(agent);
  const display = extractDisplay(agent);
  const schemaVersions = extractSchemaVersions(agent);
  const compatibilityStatus = getTouchPanelCompatibility(agent);
  const firmwareVersion =
    agent.firmwareVersion || agent.firmware_version || agent.agentVersion || null;
  const model =
    agent.model || agent.platform || agent.formFactor || agent.form_factor || 'ESP32';
  const onlineRaw = (
    agent.healthStatus ||
    agent.health_status ||
    agent.status ||
    ''
  ).toLowerCase();
  const online =
    onlineRaw === 'online' || onlineRaw === 'idle' || onlineRaw === 'busy'
      ? 'online'
      : onlineRaw === 'degraded'
        ? 'degraded'
        : onlineRaw
          ? 'offline'
          : 'unknown';

  const { map } = normalizeCaps(agent.capabilities);
  const capsObject =
    agent.capabilities && typeof agent.capabilities === 'object' && !Array.isArray(agent.capabilities)
      ? agent.capabilities
      : {
          [TOUCH_PANEL_CAPABILITY_KEY]: isTouchPanelCandidate(agent),
          [TOUCH_PANEL_SCHEMA_VERSIONS_KEY]:
            schemaVersions.length > 0
              ? schemaVersions
              : [...TOUCH_PANEL_PAYLOAD_LIMITS.SUPPORTED_SCHEMA_VERSIONS],
          display: {
            width: display.width ?? TOUCH_PANEL_PAYLOAD_LIMITS.DEFAULT_DISPLAY.width,
            height: display.height ?? TOUCH_PANEL_PAYLOAD_LIMITS.DEFAULT_DISPLAY.height,
            orientation: display.orientation,
          },
        };

  return {
    deviceId,
    name: agent.agentName || agent.displayName || agent.name || deviceId,
    model,
    firmwareVersion,
    online,
    displayWidth: display.width,
    displayHeight: display.height,
    orientation: display.orientation,
    rendererSchemaVersions: schemaVersions,
    compatibilityStatus,
    isCandidate: isTouchPanelCandidate(agent),
    capabilities: {
      ...capsObject,
      touchPanel:
        map[TOUCH_PANEL_CAPABILITY_KEY] === true || isTouchPanelCandidate(agent),
    },
    currentAssignment: assignment
      ? {
          assignmentId: assignment.id,
          panelId: assignment.panelId,
          panelName: panelNameById[assignment.panelId] || assignment.panelId,
          enabled: assignment.enabled !== false,
        }
      : null,
    raw: agent,
  };
}

export function buildDeviceSnapshot(deviceView) {
  return {
    name: deviceView.name || null,
    model: deviceView.model || null,
    displayWidth: deviceView.displayWidth ?? null,
    displayHeight: deviceView.displayHeight ?? null,
    orientation: deviceView.orientation || null,
    firmwareVersion: deviceView.firmwareVersion || null,
    rendererSchemaVersions: deviceView.rendererSchemaVersions || [],
  };
}

export function filterEligibleDevices(devices, searchText) {
  const q = (searchText || '').trim().toLowerCase();
  if (!q) return devices || [];
  return (devices || []).filter((d) => {
    const hay = [d.name, d.deviceId, d.model, d.firmwareVersion]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
}
